/**
 * The last two minutes of how the game ran on this device, for "Send diagnostics" (P3-OBS-06,
 * ADR 0035): numbers and named marks only (frame times, network timings, memory, error classes),
 * no person and nothing typed. Kept in this tab's sessionStorage, so the contact page (same origin,
 * same tab) can offer to attach it, and gone when the tab closes. Sent only if the person ticks
 * the box on the contact form, linked to their support request.
 */

export const DIAG_KEY = "obean.diag";
/** Seconds kept: the last two minutes. */
export const DIAG_SECONDS = 120;
const DIAG_MARKS = 40;
const MARK = /^[a-z][a-z0-9_:.-]{0,59}$/;

type Numbers = Record<string, number | null>;

export interface Diagnostics {
  /** The latest play (game, version, mission, mode, match). */
  play: Record<string, string>;
  place: "room" | "solo";
  slot: number;
  /** Device class, OS and browser: the same coarse facts every trace has. */
  platform: { device_class: string; os: string; browser: string };
  /** One entry a second, oldest first; `t` is seconds since `since`. */
  timeline: Numbers[];
  marks: { t: number; what: string }[];
  /** When the play started (ms since the epoch). */
  since: number;
}

let current: Diagnostics | null = null;
let savedAt = 0;
let frames: number[] = [];
let lastFrame: number | null = null;
let lastSecond: number | null = null;
let longTasks = 0;
let watching = false;

function save(force = false): void {
  if (!current) return;
  const now = Date.now();
  if (!force && now - savedAt < 5_000) return;
  savedAt = now;
  try {
    sessionStorage.setItem(DIAG_KEY, JSON.stringify(current));
  } catch {
    // No storage: nothing to attach later.
  }
}

/** Page-wide marks (tab hidden or shown, network, uncaught error classes), from the first play. */
function watchPage(): void {
  if (watching) return;
  watching = true;
  document.addEventListener("visibilitychange", () => {
    diagMark(document.visibilityState === "hidden" ? "hidden" : "visible");
  });
  window.addEventListener("online", () => {
    diagMark("online");
  });
  window.addEventListener("offline", () => {
    diagMark("offline");
  });
  window.addEventListener("error", (e) => {
    diagMark(`error:${e.error instanceof Error ? e.error.name.toLowerCase() : "unknown"}`);
  });
  window.addEventListener("pagehide", () => {
    save(true);
  });
  if (PerformanceObserver.supportedEntryTypes.includes("longtask"))
    new PerformanceObserver((list) => {
      longTasks += list.getEntries().length;
    }).observe({ type: "longtask" });
}

/** A play started: what it is. The two minutes kept are this play's. */
export function diagPlay(
  place: "room" | "solo",
  slot: number,
  play: Record<string, string>,
  platform: Diagnostics["platform"],
): void {
  watchPage();
  current = { play, place, slot, platform, timeline: [], marks: [], since: Date.now() };
  frames = [];
  lastFrame = null;
  lastSecond = null;
  save(true);
}

/**
 * One display frame; once a second it keeps the second's frame count and times plus `sampled()`
 * (the network now, from the driver).
 */
export function diagFrame(now: number, sampled: () => Numbers): void {
  if (!current) return;
  if (lastFrame !== null) {
    const ms = now - lastFrame;
    if (ms > 0 && ms < 10_000) frames.push(ms);
  }
  lastFrame = now;
  lastSecond ??= now;
  if (now - lastSecond < 1000) return;
  lastSecond = now;
  const sorted = frames.sort((a, b) => a - b);
  frames = [];
  const used = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory
    ?.usedJSHeapSize;
  current.timeline.push({
    t: Math.round((Date.now() - current.since) / 1000),
    frames: sorted.length,
    frame_ms_p95: round1(sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]),
    frame_ms_max: round1(sorted.at(-1)),
    memory_mb: used === undefined ? null : Math.round(used / 1_048_576),
    long_tasks_total: longTasks,
    ...sampled(),
  });
  while (current.timeline.length > DIAG_SECONDS) current.timeline.shift();
  save();
}

/** Something happened: a name, never free text (an error's class, a join that failed). */
export function diagMark(what: string): void {
  if (!current || !MARK.test(what)) return;
  current.marks.push({ t: Math.round((Date.now() - current.since) / 1000), what });
  while (current.marks.length > DIAG_MARKS) current.marks.shift();
  save(true);
}

const round1 = (n: number | undefined) => (n === undefined ? null : Math.round(n * 10) / 10);

/** What this tab kept, if a play was on (the contact page reads it). */
export function readDiagnostics(storage: Pick<Storage, "getItem">): Diagnostics | null {
  try {
    const raw = storage.getItem(DIAG_KEY);
    const d = raw ? (JSON.parse(raw) as Partial<Diagnostics>) : null;
    if (!d?.play || !Array.isArray(d.timeline) || !Array.isArray(d.marks)) return null;
    return d as Diagnostics;
  } catch {
    return null;
  }
}

/**
 * The trace sent with a support request (`POST /api/traces`, why "diagnostics"), its id taken from
 * the request's (`diag-OB-XXXXXX`), so support finds it with `pnpm trace -- --show`.
 */
export function diagnosticsTrace(d: Diagnostics, ticket: string): Record<string, unknown> {
  const timeline = d.timeline.slice(-DIAG_SECONDS);
  const largest = (k: string) => {
    let max: number | null = null;
    for (const s of timeline) {
      const v = s[k];
      if (typeof v === "number" && (max === null || v > max)) max = v;
    }
    return max;
  };
  const slow = timeline.filter((s) => (s["frames"] ?? 0) < 30).length;
  return {
    trace_id: `diag-${ticket}`.slice(0, 64),
    why: "diagnostics",
    place: d.place,
    play: { ...d.play, slot: d.slot },
    platform: d.platform,
    seconds: timeline.length,
    summary: {
      frame_ms_p95_max: largest("frame_ms_p95"),
      frame_ms_max: largest("frame_ms_max"),
      pct_seconds_under_30fps:
        timeline.length > 0 ? Math.round((1000 * slow) / timeline.length) / 10 : null,
      rtt_p95_max_ms: largest("input_rtt_p95_ms"),
      snapshot_gap_p95_max_ms: largest("snapshot_gap_p95_ms"),
      memory_mb_max: largest("memory_mb"),
      long_tasks: largest("long_tasks_total"),
      marks: d.marks.length,
    },
    timeline,
    marks: d.marks,
  };
}
